import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../components/layouts/Main';
import axios from 'axios';
import config from '../config';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

const ClientDetails = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Modals and forms
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const subscriptionModalRef = useRef(null);

  // Client edit form data
  const [editForm, setEditForm] = useState({
    companyName: '',
    email: '',
    contactNumber: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    status: ''
  });

  // Subscription form data
  const [subscriptionForm, setSubscriptionForm] = useState({
    packageId: '',
    startDate: new Date().toISOString().split('T')[0],
    paymentDetails: {
      amount: '',
      transactionId: '',
      paymentMethod: 'online',
      paidAt: new Date().toISOString()
    },
    isAutoRenew: false
  });

  useEffect(() => {
    if (clientId) {
      fetchClientData();
      fetchSubscriptions();
      fetchPackages();
    }
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/master/clients/details/${clientId}`);
      const clientData = response.data.client;
      setClient(clientData);
      setEditForm({
        companyName: clientData.companyName || '',
        email: clientData.email || '',
        contactNumber: clientData.contactNumber || '',
        address: clientData.address || '',
        city: clientData.city || '',
        state: clientData.state || '',
        country: clientData.country || '',
        pincode: clientData.pincode || '',
        status: clientData.status || ''
      });
    } catch (error) {
      console.error('Error fetching client:', error);
      toast.error('Failed to fetch client details');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateClient = async (e) => {
    e.preventDefault();
    setUpdateLoading(true);
    try {
      await axios.put(`${config.API_BASE_URL}/api/master/clients/${clientId}`, editForm);
      toast.success('Client details updated successfully');
      setIsEditing(false);
      fetchClientData();
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client details');
    } finally {
      setUpdateLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/master/subscriptions/client/${clientId}`);
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to fetch subscriptions');
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/master/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleSubscriptionFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith('paymentDetails.')) {
      const field = name.split('.')[1];
      setSubscriptionForm(prev => ({
        ...prev,
        paymentDetails: {
          ...prev.paymentDetails,
          [field]: value
        }
      }));
    } else {
      setSubscriptionForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handlePackageChange = (e) => {
    const packageId = e.target.value;
    const selectedPackage = packages.find(pkg => pkg._id === packageId);

    setSubscriptionForm(prev => ({
      ...prev,
      packageId,
      paymentDetails: {
        ...prev.paymentDetails,
        amount: selectedPackage ? selectedPackage.price.toString() : ''
      }
    }));
  };

  const handleSubmitSubscription = async (e) => {
    e.preventDefault();
    setSubscriptionLoading(true);

    try {
      const dataToSubmit = {
        ...subscriptionForm,
        clientId,
        endDate: new Date(new Date(subscriptionForm.startDate).getTime() +
                packages.find(p => p._id === subscriptionForm.packageId)?.duration * 24 * 60 * 60 * 1000).toISOString(),
        paymentDetails: {
          ...subscriptionForm.paymentDetails,
          amount: parseFloat(subscriptionForm.paymentDetails.amount)
        }
      };

      await axios.post(`${config.API_BASE_URL}/api/master/subscriptions`, dataToSubmit);

      toast.success('Subscription created successfully!');
      fetchSubscriptions();
      fetchClientData();
      closeSubscriptionModal();
    } catch (error) {
      console.error('Error saving subscription:', error);
      toast.error('Failed to save subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleStatusChange = async (subscriptionId, newStatus) => {
    try {
      await axios.put(`${config.API_BASE_URL}/api/master/subscriptions/${subscriptionId}/status`, {
        status: newStatus
      });
      toast.success('Subscription status updated!');
      fetchSubscriptions();
      fetchClientData();
    } catch (error) {
      console.error('Error updating subscription status:', error);
      toast.error('Failed to update subscription status');
    }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;

    try {
      await axios.put(`${config.API_BASE_URL}/api/master/subscriptions/${subscriptionId}/cancel`);
      toast.success('Subscription cancelled successfully!');
      fetchSubscriptions();
      fetchClientData();
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const openSubscriptionModal = () => {
    setSubscriptionForm({
      packageId: '',
      startDate: new Date().toISOString().split('T')[0],
      paymentDetails: {
        amount: '',
        transactionId: '',
        paymentMethod: 'online',
        paidAt: new Date().toISOString()
      },
      isAutoRenew: false
    });
    setShowSubscriptionModal(true);
  };

  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSubscriptionForm({
      packageId: '',
      startDate: new Date().toISOString().split('T')[0],
      paymentDetails: {
        amount: '',
        transactionId: '',
        paymentMethod: 'online',
        paidAt: new Date().toISOString()
      },
      isAutoRenew: false
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'd MMM yyyy');
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-success',
      expired: 'bg-danger',
      cancelled: 'bg-secondary',
      pending: 'bg-warning'
    };
    return <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>{status}</span>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <div className="text-center py-5">
          <h4>Client not found</h4>
          <Link to="/clients" className="btn btn-primary">Back to Clients</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="client-details-page">
        {/* Breadcrumb */}
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/clients">Clients</Link>
            </li>
            <li className="breadcrumb-item active">{client.companyName}</li>
          </ol>
        </nav>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-bold text-body-emphasis mb-0">Client Details</h2>
          <div className="d-flex gap-2">
            <button
              className={`btn ${isEditing ? 'btn-secondary' : 'btn-outline-primary'}`}
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? 'Cancel' : <><i className="fas fa-edit me-2" />Edit Details</>}
            </button>
            <button
              className="btn btn-primary"
              onClick={openSubscriptionModal}
            >
              <span className="fas fa-plus me-2" />
              Assign Package
            </button>
          </div>
        </div>

        {/* Client Info Card */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">{isEditing ? 'Edit Client Information' : 'Client Information'}</h5>
              </div>
              <div className="card-body">
                {isEditing ? (
                  <form onSubmit={handleUpdateClient}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="companyName"
                          value={editForm.companyName}
                          onChange={handleEditFormChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          name="email"
                          value={editForm.email}
                          onChange={handleEditFormChange}
                          required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Contact Number</label>
                        <input
                          type="text"
                          className="form-control"
                          name="contactNumber"
                          value={editForm.contactNumber}
                          onChange={handleEditFormChange}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          name="status"
                          value={editForm.status}
                          onChange={handleEditFormChange}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                        </select>
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label">Address</label>
                        <textarea
                          className="form-control"
                          name="address"
                          value={editForm.address}
                          onChange={handleEditFormChange}
                          rows="2"
                        ></textarea>
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          name="city"
                          value={editForm.city}
                          onChange={handleEditFormChange}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          className="form-control"
                          name="state"
                          value={editForm.state}
                          onChange={handleEditFormChange}
                        />
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="form-label">Pincode</label>
                        <input
                          type="text"
                          className="form-control"
                          name="pincode"
                          value={editForm.pincode}
                          onChange={handleEditFormChange}
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={updateLoading}
                      >
                        {updateLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Company Details</h6>
                      <p><strong>Company Name:</strong> {client.companyName}</p>
                      <p><strong>Subdomain:</strong> {client.subdomain}</p>
                      <p><strong>Database:</strong> {client.dbName}</p>
                      <p><strong>Status:</strong> <span className={`badge ${client.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>{client.status}</span></p>
                    </div>
                    <div className="col-md-6">
                      <h6>Contact Information</h6>
                      <p><strong>Email:</strong> {client.email || 'N/A'}</p>
                      <p><strong>Phone:</strong> {client.contactNumber || 'N/A'}</p>
                      <p><strong>Joined:</strong> {formatDate(client.createdAt)}</p>
                      {client.address && (
                        <p><strong>Address:</strong> {client.address}, {client.city}, {client.state} {client.pincode}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Subscriptions Section */}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Subscription History</h5>
            <button
              className="btn btn-sm btn-primary"
              onClick={openSubscriptionModal}
            >
              <i className="fas fa-plus me-1"></i>Add Subscription
            </button>
          </div>
          <div className="card-body">
            {subscriptions.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No subscriptions found for this client.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Package</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map(subscription => (
                      <tr key={subscription._id}>
                        <td>
                          <div>
                            <strong>{subscription.packageId.name}</strong>
                            <div className="text-muted small">₹{subscription.paymentDetails.amount}</div>
                          </div>
                        </td>
                        <td>{formatDate(subscription.startDate)}</td>
                        <td>{formatDate(subscription.endDate)}</td>
                        <td>{getStatusBadge(subscription.status)}</td>
                        <td>
                          <div className="text-muted small">
                            {subscription.paymentDetails.paymentMethod}
                          </div>
                        </td>
                        <td>
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-outline-secondary dropdown-toggle"
                              type="button"
                              data-bs-toggle="dropdown"
                            >
                              Actions
                            </button>
                            <ul className="dropdown-menu">
                              {subscription.status === 'active' && (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleStatusChange(subscription._id, 'cancelled')}
                                  >
                                    Deactivate
                                  </button>
                                </li>
                              )}
                              {subscription.status === 'cancelled' && (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => handleStatusChange(subscription._id, 'active')}
                                  >
                                    Reactivate
                                  </button>
                                </li>
                              )}
                              <li>
                                <hr className="dropdown-divider" />
                              </li>
                              <li>
                                <button
                                  className="dropdown-item text-danger"
                                  onClick={() => handleCancelSubscription(subscription._id)}
                                >
                                  Cancel Subscription
                                </button>
                              </li>
                            </ul>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Modal */}
        {showSubscriptionModal && (
          <div className="modal fade show" style={{ display: 'block' }} ref={subscriptionModalRef}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Assign New Subscription</h5>
                  <button type="button" className="btn-close" onClick={closeSubscriptionModal}></button>
                </div>
                <form onSubmit={handleSubmitSubscription}>
                  <div className="modal-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Package</label>
                        <select
                          className="form-control"
                          name="packageId"
                          value={subscriptionForm.packageId}
                          onChange={handlePackageChange}
                          required
                        >
                          <option value="">Select Package</option>
                          {packages.map(pkg => (
                            <option key={pkg._id} value={pkg._id}>
                              {pkg.name} - ₹{pkg.price}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="startDate"
                          value={subscriptionForm.startDate}
                          onChange={handleSubscriptionFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Payment Amount</label>
                        <input
                          type="number"
                          className="form-control"
                          name="paymentDetails.amount"
                          value={subscriptionForm.paymentDetails.amount}
                          onChange={handleSubscriptionFormChange}
                          step="0.01"
                          required
                          readOnly
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Payment Method</label>
                        <select
                          className="form-control"
                          name="paymentDetails.paymentMethod"
                          value={subscriptionForm.paymentDetails.paymentMethod}
                          onChange={handleSubscriptionFormChange}
                        >
                          <option value="online">Online</option>
                          <option value="offline">Offline</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cash">Cash</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Transaction ID</label>
                      <input
                        type="text"
                        className="form-control"
                        name="paymentDetails.transactionId"
                        value={subscriptionForm.paymentDetails.transactionId}
                        onChange={handleSubscriptionFormChange}
                      />
                    </div>

                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="isAutoRenew"
                          checked={subscriptionForm.isAutoRenew}
                          onChange={handleSubscriptionFormChange}
                        />
                        <label className="form-check-label">Auto Renew</label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeSubscriptionModal}
                      disabled={subscriptionLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={subscriptionLoading}
                    >
                      {subscriptionLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating...
                        </>
                      ) : (
                        'Create Subscription'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Modal backdrop */}
        {showSubscriptionModal && <div className="modal-backdrop fade show"></div>}
      </div>
    </Layout>
  );
};

export default ClientDetails;
